import type { MapLayerState, MapLayerActions } from "@/types/stores/map";

/**
 * Unified layer visibility actions
 * 
 * Provides a single API for managing visibility of all layer types:
 * - Static layers: "static:{layerId}"
 * - Data layers: "data:{layerId}"
 * - Groups: "group:{workspaceId}:{groupId}"
 * - Chunks: "chunk:{workspaceId}:{chunkId}"
 */
export function createUnifiedLayerActions(
  set: any,
  get: () => MapLayerState & MapLayerActions
) {
  return {
    /**
     * Update layer visibility using unified layer ID format
     * @param layerId - Unified layer ID (e.g., "static:municipalities", "data:my-data-input", "group:workspace1:value1")
     * @param visible - Visibility state
     */
    updateLayerVisibility: (layerId: string, visible: boolean) => {
      set(
        (state: MapLayerState) => ({
          layerVisibility: {
            ...state.layerVisibility,
            [layerId]: visible,
          },
        }),
        false,
        "map/updateLayerVisibility"
      );

      // Also update legacy visibility maps for backward compatibility
      const state = get();
      if (layerId.startsWith("static:")) {
        const staticLayerId = layerId.replace("static:", "");
        get().setStaticLayerVisibility(staticLayerId, visible);
      } else if (layerId.startsWith("data:")) {
        const dataLayerId = layerId.replace("data:", "");
        get().setDataLayerVisibility(dataLayerId, visible);
      } else if (layerId.startsWith("group:") || layerId.startsWith("chunk:")) {
        // Handle group/chunk visibility
        const parts = layerId.split(":");
        if (parts.length >= 3) {
          const workspaceId = parts[1];
          const groupId = parts.slice(2).join(":");
          const groupState = state.groups[workspaceId];
          
          // Check if this is a brick layer region (regionId format: layer-{type}-{index})
          const isBrickLayerRegion = groupId.startsWith("layer-");
          
          if (isBrickLayerRegion) {
            // For brick layer regions, update layerRegionConfigs
            const currentRegionConfigs = groupState?.layerRegionConfigs || {};
            const currentRegionConfig = currentRegionConfigs[groupId];
            
            get().setLayerRegionConfig(workspaceId, groupId, {
              ...currentRegionConfig,
              isVisible: visible,
            });
          } else {
            // For regular groups/chunks, update visibleGroups
            const currentVisibleGroups = groupState?.visibleGroups || [];
            const currentVisible = currentVisibleGroups.includes(groupId);
            
            if (currentVisible !== visible) {
              // Update visibleGroups directly
              const newVisibleGroups = visible
                ? [...currentVisibleGroups, groupId]
                : currentVisibleGroups.filter((id) => id !== groupId);
              get().setVisibleGroups(workspaceId, newVisibleGroups);
            }
          }
        }
      }
    },

    /**
     * Get layer visibility by unified layer ID
     * @param layerId - Unified layer ID
     * @returns Visibility state, defaults to false if not found
     */
    getLayerVisibilityById: (layerId: string): boolean => {
      const state = get();
      
      // Check unified visibility map first
      if (layerId in state.layerVisibility) {
        return state.layerVisibility[layerId];
      }

      // Fall back to legacy maps for backward compatibility
      if (layerId.startsWith("static:")) {
        const staticLayerId = layerId.replace("static:", "");
        return state.staticLayers[staticLayerId] ?? false;
      } else if (layerId.startsWith("data:")) {
        const dataLayerId = layerId.replace("data:", "");
        return state.dataLayers[dataLayerId] ?? true; // Data layers default to visible
      } else if (layerId.startsWith("group:") || layerId.startsWith("chunk:")) {
        const parts = layerId.split(":");
        if (parts.length >= 3) {
          const workspaceId = parts[1];
          const groupId = parts.slice(2).join(":");
          const groupState = state.groups[workspaceId];
          
          // Check if this is a brick layer region (regionId format: layer-{type}-{index})
          const isBrickLayerRegion = groupId.startsWith("layer-");
          
          if (isBrickLayerRegion) {
            // For brick layer regions, check layerRegionConfigs
            const regionConfig = groupState?.layerRegionConfigs?.[groupId];
            return regionConfig?.isVisible !== false; // Default to true if not specified
          } else {
            // For regular groups/chunks, check visibleGroups
            return groupState?.visibleGroups?.includes(groupId) ?? false;
          }
        }
      }

      return false;
    },
  };
}

